import type { ReactNode } from 'react';
import Icon from '@mui/material/Icon';
import type { SxProps, Theme } from '@mui/material/styles';

export interface FeatherIconProps {
  /** The glyph name without the `icon-` prefix, e.g. `home`, `briefcase`, `arrow-left`. */
  readonly glyph: string;
  readonly fontSize?: 'inherit' | 'small' | 'medium' | 'large';
  readonly sx?: SxProps<Theme>;
  /** Give the glyph a name only when it stands alone; beside a label it is decoration. */
  readonly 'aria-label'?: string;
  readonly className?: string;
}

/**
 * The legacy admin's Feather icon FONT, rendered through MUI (SDD-012, U-4).
 *
 * This is MUI's own `Icon` — the component for font icons — pointed at the `feather` face that
 * `@eis/theme` registers from the frozen font files. `className="icon-<glyph>"` is exactly the
 * class the legacy markup uses (`feather-icon-font.css`, kept verbatim), so a sidebar drawn with
 * this component uses the same glyphs from the same file as the window the user came from.
 *
 * No dependency is added: the font is a static asset, the component is MUI's. In the Berry
 * variant the shell resolves the same manifest names to `@mui/icons-material` SVGs instead
 * (`icon-map.json`), so one manifest serves both looks.
 */
export function FeatherIcon({
  glyph,
  fontSize = 'small',
  sx,
  className,
  'aria-label': ariaLabel,
}: FeatherIconProps): ReactNode {
  return (
    <Icon
      baseClassName="feather"
      className={className === undefined ? `icon-${glyph}` : `icon-${glyph} ${className}`}
      fontSize={fontSize}
      // Decorative unless named: beside a label the glyph would otherwise be read out twice.
      aria-hidden={ariaLabel === undefined ? true : undefined}
      aria-label={ariaLabel}
      role={ariaLabel === undefined ? undefined : 'img'}
      data-feather-glyph={glyph}
      sx={sx}
    />
  );
}
